import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { reservasApi, reservasAdminApi, api } from '../../lib/api';
import { Footer } from '../../components/footer';
import { CheckinPanel } from '../../components/CheckinPanel';
import { FormPagoReserva } from '../reservar/FormPagoReserva';
import { useAuth } from '../../lib/auth';
import { puedeOperarNegocio } from '../../lib/permisos';

interface RecursoInfo { id: number; nombre: string; capacidad: number; }
interface ZonaInfo { id: number; nombre: string; permiteSeleccionMesa?: boolean; recursos?: RecursoInfo[]; }

const ESTADO_COLORS: Record<string, string> = {
  pendiente:  'bg-amber-100 text-amber-800 border-amber-300',
  confirmada: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelada:  'bg-rose-100 text-rose-800 border-rose-300',
  no_show:    'bg-slate-200 text-slate-700 border-slate-400',
  atendida:   'bg-blue-100 text-blue-800 border-blue-300',
  en_mesa:    'bg-indigo-100 text-indigo-800 border-indigo-300',
};

const ESTADO_LABELS: Record<string, string> = {
  pendiente:  'Pendiente',
  confirmada: 'Confirmada',
  cancelada:  'Cancelada',
  no_show:    'No-show',
  atendida:   'Atendida',
  en_mesa:    'En mesa',
};

const CONTEXTO_OPTS = ['mesa', 'terraza', 'barra', 'llevar', 'delivery'];

const FIELD_LABELS: Record<string, string> = {
  fechaReserva:             'Fecha',
  horaReserva:              'Hora',
  numPersonas:              'Personas',
  tipoEvento:               'Tipo de evento',
  contexto:                 'Contexto',
  nombreCliente:            'Nombre',
  telefono:                 'Teléfono',
  email:                    'Email',
  requerimientosEspeciales: 'Requerimientos',
  notasInternas:            'Notas internas',
};

function fmtFechaHora(fecha: string, hora: string) {
  const d = new Date(`${fecha}T${hora}`);
  return d.toLocaleString('es-VE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function fmtDateTime(s?: string) {
  if (!s) return '—';
  return new Date(s).toLocaleString('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function hhmm(s?: string) {
  if (!s) return '';
  return s.length >= 5 ? s.slice(0, 5) : s;
}

// Quién hizo cada evento del historial. El backend ya devuelve usuarioNombre
// (join con crm_users); si no hay usuario, distinguimos web pública vs sistema.
function autorEvento(e: any): string {
  if (e.usuarioNombre) return e.usuarioNombre;
  if (e.usuarioEmail) return e.usuarioEmail;
  if (e.usuarioId) return 'Usuario';
  if (e.tipo === 'creada') return 'Cliente (web)';
  return 'Sistema';
}

export default function ReservaDetail() {
  const { user } = useAuth();
  const puedeOperar = puedeOperarNegocio(user);
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [showCheckin, setShowCheckin] = useState(false);
  const [showReabrir, setShowReabrir] = useState(false);
  const [showRevertir, setShowRevertir] = useState(false);
  const [motivoRevertir, setMotivoRevertir] = useState("");
  const [reabrirForm, setReopenForm] = useState({ fechaReserva: '', horaReserva: '', numPersonas: 2, confirmarInmediatamente: false });

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [editError, setEditError] = useState<string | null>(null);
  const [zonas, setZonas] = useState<ZonaInfo[]>([]);
  const [showPagoTel, setShowPagoTel] = useState(false);

  async function revertirAPendiente() {
    if (!id) return;
    setActionLoading(true);
    try {
      await reservasApi.revertirAPendiente(Number(id), motivoRevertir.trim() || undefined);
      setShowRevertir(false);
      setMotivoRevertir("");
      await load();
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  }

  async function load() {
    if (!id) return;
    setLoading(true);
    try {
      const r = await reservasApi.detail(Number(id));
      setData(r);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]);

  async function runAction(fn: () => Promise<any>) {
    if (!id || actionLoading) return;
    setActionLoading(true);
    try {
      await fn();
      await load();
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  }

  function startEdit() {
    const r = data.reserva;
    setEditForm({
      fechaReserva: r.fechaReserva,
      horaReserva: hhmm(r.horaReserva),
      numPersonas: r.numPersonas,
      tipoEvento: r.tipoEvento ?? '',
      contexto: r.contexto ?? 'mesa',
      nombreCliente: r.nombreCliente,
      telefono: r.telefono ?? '',
      email: r.email ?? '',
      requerimientosEspeciales: r.requerimientosEspeciales ?? '',
      notasInternas: r.notasInternas ?? '',
      zonaId: r.zonaId ?? undefined,
      recursoId: r.recursoId ?? undefined,
    });
    setEditError(null);
    setEditing(true);
    // Cargar zonas con recursos si no se han cargado
    if (zonas.length === 0) {
      api<any>('/v1/reservas/reservar-info').then(info => {
        if (info?.zonas) setZonas(info.zonas);
      }).catch(() => {});
    }
  }

  function cancelEdit() {
    setEditing(false);
    setEditForm({});
    setEditError(null);
  }

  async function saveEdit() {
    if (!id) return;
    setEditError(null);

    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const f = new Date(editForm.fechaReserva + 'T00:00:00');
    if (f < hoy) {
      setEditError('La fecha no puede ser pasada');
      return;
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(editForm.horaReserva)) {
      setEditError('Hora inválida (formato HH:MM)');
      return;
    }
    if (editForm.numPersonas < 1 || editForm.numPersonas > 50) {
      setEditError('Personas debe estar entre 1 y 50');
      return;
    }
    if (!editForm.nombreCliente?.trim()) {
      setEditError('Nombre es requerido');
      return;
    }

    const original = data.reserva;
    const dto: any = {};
    // Campos de texto/número: comparar y enviar solo los que cambiaron
    const camposTexto = ['fechaReserva','horaReserva','numPersonas','tipoEvento','contexto','nombreCliente','telefono','email','requerimientosEspeciales','notasInternas'];
    for (const k of camposTexto) {
      const nuevo = editForm[k];
      const viejo = k === 'horaReserva' ? hhmm(original[k]) : original[k];
      if (nuevo !== (viejo ?? '') && !(nuevo === '' && (viejo === null || viejo === undefined))) {
        dto[k] = nuevo === '' ? null : nuevo;
      }
    }
    // Zona y recurso: siempre incluir si cambiaron (pueden ser undefined/null)
    if (editForm.zonaId !== (original.zonaId ?? undefined)) dto.zonaId = editForm.zonaId ?? null;
    if (editForm.recursoId !== (original.recursoId ?? undefined)) dto.recursoId = editForm.recursoId ?? null;

    if (Object.keys(dto).length === 0) {
      cancelEdit();
      return;
    }

    setActionLoading(true);
    try {
      await reservasApi.update(Number(id), dto);
      setEditing(false);
      setEditForm({});
      await load();
    } catch (e: any) {
      setEditError(e.message);
    } finally {
      setActionLoading(false);
    }
  }

  function renderEditEvent(notas?: string) {
    if (!notas) return null;
    try {
      const diff = JSON.parse(notas);
      return (
        <ul className="text-xs text-slate-600 mt-1 space-y-0.5">
          {Object.entries(diff).map(([k, v]: [string, any]) => (
            <li key={k}>
              <span className="text-slate-500">{FIELD_LABELS[k] ?? k}:</span>{' '}
              <span className="line-through text-slate-400">{String(v.de ?? '—')}</span>
              {' → '}
              <span className="text-slate-800 font-medium">{String(v.a ?? '—')}</span>
            </li>
          ))}
        </ul>
      );
    } catch {
      return <div className="text-xs text-slate-600 mt-0.5">{notas}</div>;
    }
  }

  if (loading) return <div className="p-12 text-center text-slate-500">Cargando…</div>;
  if (error) return <div className="p-12 text-center text-rose-700">Error: {error}</div>;
  if (!data) return null;

  const { reserva, eventos } = data;
  const idNum = Number(id);

  // Permisos de estado: solo admin, gerencia, supervisor, recepción
  const canConfirmar = puedeOperar && reserva.estado === 'pendiente';
  const canCancelar  = puedeOperar && ['pendiente', 'confirmada'].includes(reserva.estado);
  const canNoShow    = puedeOperar && reserva.estado === 'confirmada';
  const canAtendida  = puedeOperar && ['confirmada', 'pendiente', 'en_mesa'].includes(reserva.estado);
  const canEditar    = puedeOperar && ['pendiente', 'confirmada'].includes(reserva.estado) && !editing;
  const canCheckin   = ['pendiente', 'confirmada'].includes(reserva.estado);
  const canReabrir   = ['cancelada', 'no_show'].includes(reserva.estado);
  // Solo supervisor/admin pueden revertir confirmada → pendiente
  const canRevertir  = puedeOperar && reserva.estado === 'confirmada' && user?.role !== 'tienda_recepcion';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/reservas" className="text-sm text-slate-500 hover:text-slate-700">← Reservas</Link>
            <h1 className="text-xl font-bold text-slate-900">Reserva #{reserva.id}</h1>
            <span className={`inline-block text-sm px-3 py-1 rounded-full border ${ESTADO_COLORS[reserva.estado] ?? ''}`}>
              {ESTADO_LABELS[reserva.estado] ?? reserva.estado}
            </span>
          </div>
          {canEditar && (
            <button
              onClick={startEdit}
              className="px-4 py-2 bg-brand-600 text-white text-sm rounded-md hover:bg-brand-700 font-medium"
            >
              ✎ Editar
            </button>
          )}
          {canReabrir && (
            <button
              onClick={() => {
                const r = data.reserva;
                setReopenForm({ fechaReserva: r.fechaReserva ?? '', horaReserva: r.horaReserva?.slice(0,5) ?? '', numPersonas: r.numPersonas ?? 2, confirmarInmediatamente: false });
                setShowReabrir(true);
              }}
              className="px-4 py-2 bg-amber-500 text-white text-sm rounded-md hover:bg-amber-600 font-medium"
            >
              ↺ Reabrir / Reprogramar
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                {editing ? 'Editando reserva' : 'Datos de la reserva'}
              </h2>
              {editing && (
                <div className="flex gap-2">
                  <button
                    onClick={saveEdit}
                    disabled={actionLoading}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {actionLoading ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                  <button
                    onClick={cancelEdit}
                    disabled={actionLoading}
                    className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded hover:bg-slate-100"
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>

            {editError && (
              <div className="mb-3 p-2 bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded">
                {editError}
              </div>
            )}

            {!editing ? (
              <>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Field label="Cuándo" value={fmtFechaHora(reserva.fechaReserva, reserva.horaReserva)} />
                  <Field label="Personas" value={String(reserva.numPersonas)} />
                  <Field label="Tipo de evento" value={reserva.tipoEvento} />
                  <Field label="Contexto" value={reserva.contexto} />
                  {/* Zona y pista/mesa asignada */}
                  {(reserva as any).zonaNombre && (
                    <Field label="Zona" value={(reserva as any).zonaNombre} />
                  )}
                  {(reserva as any).recursoNombre && (
                    <Field
                      label={String((reserva as any).zonaNombre ?? "").toLowerCase().includes("bowl") ? "Pista" : "Mesa"}
                      value={<span className="font-semibold text-brand-700">{(reserva as any).recursoNombre}</span>}
                    />
                  )}
                  <Field label="Origen" value={reserva.origen} />
                  {reserva.codCliente && <Field label="Cliente CRM" value={
                    <Link to={`/cliente360/${reserva.codCliente}`} className="text-brand-700 hover:underline">{reserva.codCliente}</Link>
                  } />}
                  {reserva.estado === 'en_mesa' && reserva.mesaId && (
                    <Field label="Mesa asignada" value={`Mesa #${reserva.mesaId}`} />
                  )}
                  {reserva.checkinAt && <Field label="Check-in" value={fmtDateTime(reserva.checkinAt)} />}
                </dl>

                <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Field label="Nombre" value={reserva.nombreCliente} />
                  <Field label="Teléfono" value={reserva.telefono} />
                  <Field label="Email" value={reserva.email} />
                </div>

                {reserva.requerimientosEspeciales && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Requerimientos especiales</div>
                    <div className="text-sm text-slate-900 bg-amber-50 border border-amber-200 rounded p-2">
                      {reserva.requerimientosEspeciales}
                    </div>
                  </div>
                )}
                {reserva.notasInternas && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">Notas internas</div>
                    <div className="text-sm text-slate-700">{reserva.notasInternas}</div>
                  </div>
                )}
                {reserva.motivoCancelacion && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-xs text-rose-700 mb-1">Motivo de cancelación</div>
                    <div className="text-sm text-rose-900 bg-rose-50 border border-rose-200 rounded p-2">
                      {reserva.motivoCancelacion}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <EditForm form={editForm} setForm={setEditForm} zonas={zonas} />
            )}
          </section>

          <section className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Historial</h2>
            {eventos?.length === 0 ? (
              <p className="text-sm text-slate-500">Sin eventos registrados.</p>
            ) : (
              <ol className="space-y-2">
                {eventos.map((e: any) => (
                  <li key={e.id} className="flex gap-3 text-sm border-l-2 border-slate-200 pl-3 py-1">
                    <div className="text-xs text-slate-500 w-32 shrink-0">{fmtDateTime(e.createdAt)}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-900 font-medium">{e.tipo}</span>
                        <span className="text-xs text-slate-400 shrink-0">{autorEvento(e)}</span>
                      </div>
                      {e.estadoAnterior && e.estadoNuevo && e.estadoAnterior !== e.estadoNuevo && (
                        <div className="text-xs text-slate-500">
                          {e.estadoAnterior} → {e.estadoNuevo}
                        </div>
                      )}
                      {e.tipo === 'editada' ? renderEditEvent(e.notas) :
                        e.notas && <div className="text-xs text-slate-600 mt-0.5">{e.notas}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <div className="space-y-4">
          <ClienteCRMCard reservaId={idNum} reserva={reserva} disabled={editing} onLinked={load} />

          <section className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Acciones</h2>
            <div className="flex flex-col gap-2">
              <button
                disabled={!canConfirmar || actionLoading || editing}
                onClick={() => runAction(() => reservasApi.confirmar(idNum))}
                className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
              >
                ✓ Confirmar
              </button>

              {/* Revertir confirmada → pendiente (solo supervisor/admin) */}
              {canRevertir && !showRevertir && (
                <button
                  onClick={() => setShowRevertir(true)}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-40 text-sm font-medium"
                >
                  ↩ Revertir a pendiente
                </button>
              )}
              {showRevertir && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                    ⚠️ Advertencia: esta reserva estaba confirmada
                  </p>
                  <p className="text-xs text-amber-700">
                    Al revertirla, el cliente deberá confirmar nuevamente y se notificará el cambio en el historial.
                  </p>
                  <textarea
                    className="w-full border border-amber-300 rounded px-2 py-1 text-xs text-slate-700 resize-none"
                    rows={2}
                    placeholder="Motivo del cambio (opcional)"
                    value={motivoRevertir}
                    onChange={e => setMotivoRevertir(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button onClick={revertirAPendiente} disabled={actionLoading}
                      className="flex-1 px-3 py-1.5 bg-amber-500 text-white text-xs rounded hover:bg-amber-600 disabled:opacity-50 font-medium">
                      {actionLoading ? "..." : "Confirmar reversión"}
                    </button>
                    <button onClick={() => { setShowRevertir(false); setMotivoRevertir(""); }}
                      className="px-3 py-1.5 border border-slate-300 text-slate-600 text-xs rounded hover:bg-slate-50">
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {canCheckin && !showCheckin && (
                <button
                  disabled={actionLoading || editing}
                  onClick={() => setShowCheckin(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                >
                  ✓ Cliente llegó · Asignar mesa
                </button>
              )}

              {showCheckin && (
                <CheckinPanel
                  reservaId={idNum}
                  sucursalId={reserva.sucursalId}
                  numPersonas={reserva.numPersonas}
                  onDone={() => { setShowCheckin(false); load(); }}
                  onCancel={() => setShowCheckin(false)}
                />
              )}

              <button
                disabled={!canAtendida || actionLoading || editing}
                onClick={() => runAction(() => reservasApi.atendida(idNum))}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
              >
                ✓ Marcar como atendida
              </button>
              <button
                disabled={!canNoShow || actionLoading || editing}
                onClick={() => runAction(() => reservasApi.noShow(idNum))}
                className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
              >
                ✗ No-show
              </button>

              {!showCancel ? (
                <button
                  disabled={!canCancelar || actionLoading || editing}
                  onClick={() => setShowCancel(true)}
                  className="px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                >
                  ✗ Cancelar reserva
                </button>
              ) : (
                <div className="border border-rose-300 bg-rose-50 rounded p-3 space-y-2">
                  <label className="block text-xs text-rose-800">Motivo (opcional)</label>
                  <textarea
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    rows={2}
                    className="w-full border border-rose-300 rounded px-2 py-1 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => runAction(async () => {
                        await reservasApi.cancelar(idNum, motivo || undefined);
                        setShowCancel(false);
                        setMotivo('');
                      })}
                      disabled={actionLoading}
                      className="flex-1 px-3 py-1.5 bg-rose-600 text-white rounded text-sm hover:bg-rose-700 disabled:opacity-40"
                    >
                      Confirmar cancelación
                    </button>
                    <button
                      onClick={() => { setShowCancel(false); setMotivo(''); }}
                      disabled={actionLoading}
                      className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded text-sm hover:bg-slate-100"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Reportar pago por teléfono — solo si la zona requiere pago */}
          {puedeOperar && (reserva as any).requierePago && reserva.estado !== 'cancelada' && reserva.estado !== 'auto_cancelada' && reserva.estado !== 'confirmada' && reserva.estado !== 'atendida' && (
            <section className="bg-white rounded-lg border border-slate-200 p-5">
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Pago telefónico</h2>
              {!showPagoTel ? (
                <button onClick={() => setShowPagoTel(true)}
                  className="w-full px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm font-medium">
                  Reportar pago por teléfono
                </button>
              ) : (
                <div>
                  <FormPagoReserva
                    reservaId={idNum}
                    origen="telefono"
                    montoSugerido={(reserva as any).montoReservaUsd}
                    instrucciones={(reserva as any).instruccionesPago}
                    defaults={{
                      nombre: reserva.nombreCliente,
                      telefono: reserva.telefono,
                      email: reserva.email,
                    }}
                    onReportado={() => { setShowPagoTel(false); load(); }}
                  />
                  <button onClick={() => setShowPagoTel(false)}
                    className="mt-2 w-full px-3 py-1.5 border border-slate-300 text-slate-600 text-sm rounded hover:bg-slate-50">
                    Cancelar
                  </button>
                </div>
              )}
            </section>
          )}

          <section className="bg-white rounded-lg border border-slate-200 p-5">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Metadatos</h2>
            <dl className="space-y-1 text-xs">
              <Field label="Creada" value={fmtDateTime(reserva.createdAt)} />
              <Field label="Actualizada" value={fmtDateTime(reserva.updatedAt)} />
              {reserva.confirmadaAt && <Field label="Confirmada" value={fmtDateTime(reserva.confirmadaAt)} />}
              {reserva.canceladaAt && <Field label="Cancelada" value={fmtDateTime(reserva.canceladaAt)} />}
              {reserva.atendidaAt && <Field label="Atendida" value={fmtDateTime(reserva.atendidaAt)} />}
            </dl>
          </section>
        </div>
      </main>
      <Footer />

      {/* Modal: Reabrir / Reprogramar */}
      {showReabrir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-lg text-slate-900">↺ Reabrir / Reprogramar reserva</h3>
            <p className="text-sm text-slate-500">
              Esta reserva fue cancelada. Puedes reprogramarla con nueva fecha/hora.
              Al reabrir, <strong>no aplica política automática</strong> — no se cancelará sola.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Nueva fecha</label>
                <input type="date" value={reabrirForm.fechaReserva}
                  onChange={e => setReopenForm(f => ({ ...f, fechaReserva: e.target.value }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Hora</label>
                <input type="time" value={reabrirForm.horaReserva}
                  onChange={e => setReopenForm(f => ({ ...f, horaReserva: e.target.value }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Personas</label>
                <input type="number" min={1} max={50} value={reabrirForm.numPersonas}
                  onChange={e => setReopenForm(f => ({ ...f, numPersonas: Number(e.target.value) }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-center" />
              </div>
            </div>
            <label className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
              <div
                onClick={() => setReopenForm(f => ({ ...f, confirmarInmediatamente: !f.confirmarInmediatamente }))}
                className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${reabrirForm.confirmarInmediatamente ? "bg-emerald-500" : "bg-slate-300"}`}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${reabrirForm.confirmarInmediatamente ? "translate-x-5" : "translate-x-0"}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-emerald-800">Confirmar inmediatamente</p>
                <p className="text-xs text-emerald-600">La reserva queda confirmada al reabrirla.</p>
              </div>
            </label>
            <div className="flex gap-3">
              <button onClick={() => setShowReabrir(false)}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50">
                Cancelar
              </button>
              <button
                disabled={actionLoading}
                onClick={() => runAction(() => reservasApi.reabrir(idNum, reabrirForm).then(r => { setShowReabrir(false); return r; }))}
                className="flex-1 py-2.5 bg-amber-500 text-white text-sm rounded-lg font-medium hover:bg-amber-600 disabled:opacity-40">
                {actionLoading ? 'Reabriendo…' : reabrirForm.confirmarInmediatamente ? 'Reabrir y confirmar' : 'Reabrir como pendiente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 text-right">{value || '—'}</dd>
    </div>
  );
}

// Tarjeta de cliente del CRM: muestra el vínculo o permite asociarlo.
function ClienteCRMCard({ reservaId, reserva, disabled, onLinked }: {
  reservaId: number; reserva: any; disabled?: boolean; onLinked: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [busco, setBusco] = useState(false);
  const [sel, setSel] = useState<any>(null);
  const [actualizar, setActualizar] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const vinculado = !!reserva.codCliente;

  function abrir() {
    setOpen(true);
    setQ(reserva.nombreCliente ?? '');
    setResultados([]);
    setBusco(false);
    setSel(null);
    setActualizar(true);
    setErr(null);
  }
  function cerrar() {
    setOpen(false);
    setErr(null);
  }

  async function buscar() {
    const t = q.trim();
    if (t.length < 3) { setErr('Escribe al menos 3 caracteres'); return; }
    setErr(null);
    setBuscando(true);
    try {
      const r = await reservasAdminApi.buscarCliente(t);
      setResultados(r);
      setBusco(true);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBuscando(false);
    }
  }

  async function confirmar() {
    if (!sel) return;
    setGuardando(true);
    setErr(null);
    try {
      await reservasAdminApi.vincularCliente(reservaId, {
        codCliente: sel.codCliente,
        actualizarDatos: actualizar,
      });
      setOpen(false);
      await onLinked();
    } catch (e: any) {
      setErr(e.message);
      setGuardando(false);
    }
  }

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Cliente CRM</h2>

      {vinculado ? (
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm">
            <div className="text-slate-900 font-medium">{reserva.nombreCliente}</div>
            <Link to={`/cliente360/${reserva.codCliente}`} className="text-xs text-brand-700 hover:underline">
              Cliente #{reserva.codCliente} →
            </Link>
          </div>
          {!open && (
            <button
              disabled={disabled}
              onClick={abrir}
              className="text-xs px-2 py-1 border border-slate-300 text-slate-600 rounded hover:bg-slate-100 disabled:opacity-40"
            >
              Cambiar
            </button>
          )}
        </div>
      ) : (
        !open && (
          <>
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
              Esta reserva no está asociada a un cliente del CRM.
            </div>
            <button
              disabled={disabled}
              onClick={abrir}
              className="w-full px-4 py-2 bg-brand-600 text-white text-sm rounded-md hover:bg-brand-700 font-medium disabled:opacity-40"
            >
              Asociar cliente existente
            </button>
          </>
        )
      )}

      {open && (
        <div className="mt-3 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
              placeholder="Nombre, teléfono, email o documento…"
              className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={buscar}
              disabled={buscando}
              className="px-3 py-1.5 bg-brand-700 text-white text-sm rounded-md hover:bg-brand-800 disabled:opacity-40"
            >
              {buscando ? '…' : 'Buscar'}
            </button>
          </div>

          {err && <div className="text-xs text-rose-700">{err}</div>}

          {busco && resultados.length === 0 && !buscando && (
            <div className="text-xs text-slate-500">Sin resultados. Probá con otro dato (teléfono, email o documento).</div>
          )}

          {resultados.length > 0 && (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {resultados.map((c) => {
                const elegido = sel?.codCliente === c.codCliente;
                return (
                  <button
                    key={c.codCliente}
                    onClick={() => setSel(c)}
                    className={`w-full text-left border rounded-lg p-2 text-sm transition-colors ${
                      elegido ? 'border-brand-600 ring-2 ring-brand-300 bg-brand-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="font-medium text-slate-900">{c.nombreCliente}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
                      <span>#{c.codCliente}</span>
                      {(c.telefono1 || c.mobil) && <span>{c.telefono1 || c.mobil}</span>}
                      {c.email && <span>{c.email}</span>}
                      {c.cif && <span>{c.cif}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {sel && (
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={actualizar}
                onChange={(e) => setActualizar(e.target.checked)}
                className="mt-0.5"
              />
              <span>Actualizar nombre y contacto de la reserva con los datos del cliente del CRM</span>
            </label>
          )}

          <div className="flex gap-2 pt-1">
            <button
              onClick={confirmar}
              disabled={!sel || guardando}
              className="flex-1 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-40"
            >
              {guardando ? 'Asociando…' : 'Asociar cliente'}
            </button>
            <button
              onClick={cerrar}
              disabled={guardando}
              className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded hover:bg-slate-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function EditForm({ form, setForm, zonas }: { form: any; setForm: (f: any) => void; zonas?: ZonaInfo[] }) {
  function set(k: string, v: any) {
    setForm((prev: any) => ({ ...prev, [k]: v }));
  }
  const zonaObj = (zonas ?? []).find(z => z.id === form.zonaId);
  const recursos = zonaObj?.recursos ?? [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <Input label="Fecha" type="date" value={form.fechaReserva} onChange={(v) => set('fechaReserva', v)} />
      <Input label="Hora" type="time" value={form.horaReserva} onChange={(v) => set('horaReserva', v)} />
      <Input label="Personas" type="number" min={1} max={50} value={form.numPersonas} onChange={(v) => set('numPersonas', Number(v))} />
      <Input label="Tipo de evento" type="text" value={form.tipoEvento} onChange={(v) => set('tipoEvento', v)} />
      <Select label="Contexto" value={form.contexto} options={CONTEXTO_OPTS} onChange={(v) => set('contexto', v)} />
      {/* Zona */}
      {(zonas ?? []).length > 0 ? (
        <div>
          <label className="block text-xs text-slate-500 mb-1">Zona</label>
          <select value={form.zonaId ?? ""} onChange={e => { set('zonaId', e.target.value ? Number(e.target.value) : undefined); set('recursoId', undefined); }}
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
            <option value="">Sin preferencia</option>
            {(zonas ?? []).map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
          </select>
        </div>
      ) : <div />}
      <Input label="Nombre" type="text" value={form.nombreCliente} onChange={(v) => set('nombreCliente', v)} required />
      <Input label="Teléfono" type="tel" value={form.telefono} onChange={(v) => set('telefono', v)} />
      <Input label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} />
      {/* Recurso/Mesa cuando zona lo permite */}
      {zonaObj?.permiteSeleccionMesa && recursos.length > 0 ? (
        <div className="sm:col-span-2">
          <label className="block text-xs text-slate-500 mb-1.5">
            {zonaObj.nombre.toLowerCase().includes('bowl') ? 'Pista' : 'Mesa'}
            <span className="text-slate-400 ml-1">(opcional)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {recursos.map(r => (
              <button key={r.id} type="button"
                onClick={() => set('recursoId', form.recursoId === r.id ? undefined : r.id)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${form.recursoId === r.id ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-700 border-slate-300 hover:border-brand-400"}`}>
                {r.nombre}
              </button>
            ))}
          </div>
        </div>
      ) : <div />}
      <div className="sm:col-span-2">
        <label className="block text-xs text-slate-500 mb-1">Requerimientos especiales</label>
        <textarea rows={2} value={form.requerimientosEspeciales}
          onChange={(e) => set('requerimientosEspeciales', e.target.value)}
          className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs text-slate-500 mb-1">Notas internas</label>
        <textarea rows={2} value={form.notasInternas}
          onChange={(e) => set('notasInternas', e.target.value)}
          className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
      </div>
    </div>
  );
}

function Input({ label, type, value, onChange, required, min, max }: {
  label: string;
  type: string;
  value: any;
  onChange: (v: string) => void;
  required?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}{required && <span className="text-rose-600">*</span>}</label>
      <input
        type={type}
        value={value ?? ''}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </div>
  );
}

function Select({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        {options.map((o: string) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
